import React, { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { TslNewsletterService } from "services/api-workers-service"
import type { TslNewsletterSubscriber } from "services/api-workers-service"
import { SentryService } from "services/SentryService"
import { Row } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import Input from "ui/components/atoms/input"
import { P } from "ui/components/atoms/typography"
import Alert from "ui/components/molecules/alert"
import Modal from "ui/components/molecules/modal"

const SubscriberDeleteModal = ({
  subscriber,
  setSubscriber,
  onDeleted,
}: {
  subscriber: TslNewsletterSubscriber | null
  setSubscriber: (e: TslNewsletterSubscriber | null) => void
  onDeleted?: () => void
}) => {
  const queryClient = useQueryClient()
  const [confirmEmail, setConfirmEmail] = useState("")
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setConfirmEmail("")
    setError(null)
  }, [subscriber])

  const deleteMutation = useMutation({
    mutationFn: (id: string) => TslNewsletterService.deleteSubscriber(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tsl", "subscribers"] })
      setSubscriber(null)
      onDeleted?.()
    },
    onError: e => {
      SentryService.error("[ERROR TslNewsletterService.deleteSubscriber]:", e)
      setError("Nie udało się usunąć subskrybenta. Spróbuj ponownie.")
    },
  })

  const matches = !!subscriber && confirmEmail.trim() === subscriber.email

  return (
    <Modal
      show={!!subscriber}
      setShow={() => setSubscriber(null)}
      type="center"
      title="Usuń subskrybenta (RODO)"
      text="Trwałe usunięcie danych — operacja nieodwracalna."
      $maxWidth="500px"
    >
      <>
        <Alert $type="warning" $marginBottom="S">
          Żądanie RODO right-to-be-forgotten. Wszystkie dane subskrybenta zostaną natychmiast
          usunięte z bazy.
        </Alert>
        {subscriber && (
          <P $marginBottom="S">
            Aby potwierdzić, wpisz email: <strong>{subscriber.email}</strong>
          </P>
        )}
        <Input
          name="confirmEmail"
          label="Email subskrybenta"
          value={confirmEmail}
          onChange={({ value }) => setConfirmEmail(value)}
          $marginBottom="S"
        />
        {error && (
          <Alert $type="error" $small $marginBottom="S">
            {error}
          </Alert>
        )}
        <Row $justifyContent="flex-end">
          <Button
            $type="secondary"
            onClick={() => setSubscriber(null)}
            $marginRight="XS"
            disabled={deleteMutation.isPending}
          >
            Anuluj
          </Button>
          <Button
            icon="trash"
            disabled={!matches || deleteMutation.isPending}
            loading={deleteMutation.isPending}
            onClick={() => subscriber && deleteMutation.mutate(subscriber.id)}
          >
            Usuń trwale
          </Button>
        </Row>
      </>
    </Modal>
  )
}

export default SubscriberDeleteModal
